
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import LazyImage from "@/components/ui/lazy-image";

interface ProductGalleryProps {
  product: {
    name: string;
    images: string[];
  };
}

export default function ProductGallery({ product }: ProductGalleryProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const [preloadedImages, setPreloadedImages] = useState<Set<number>>(new Set());
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    // Preload next and previous images
    const preloadImage = (index: number) => {
      if (index >= 0 && index < product.images.length && !preloadedImages.has(index)) {
        const img = new Image();
        img.src = product.images[index];
        img.onload = () => {
          setPreloadedImages(prev => new Set([...prev, index]));
          console.log(`Preloaded image ${index + 1} of ${product.images.length}`);
        };
        img.onerror = () => {
          console.error(`Failed to preload image ${index + 1} of ${product.images.length}`);
        };
      }
    };

    // Preload next image
    preloadImage(currentImageIndex + 1);
    // Preload previous image
    preloadImage(currentImageIndex - 1);
  }, [currentImageIndex, product.images, preloadedImages]);

  useEffect(() => {
    if (imageRef.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const img = entry.target as HTMLImageElement;
              img.loading = "lazy";
              console.log(`Loading image: ${img.src}`);
            }
          });
        },
        { threshold: 0.1 }
      );

      observer.observe(imageRef.current);
      return () => observer.disconnect();
    }
  }, []);

  const handleImageLoad = (index: number) => {
    setLoadedImages(prev => new Set([...prev, index]));
    console.log(`Successfully loaded image ${index + 1} of ${product.images.length}`);
  };

  const handlePrevious = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? product.images.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentImageIndex((prev) => 
      prev === product.images.length - 1 ? 0 : prev + 1
    );
  };

  if (!product.images.length) {
    return null;
  }

  return (
    <div className="relative mb-8">
      <div className="relative">
        <LazyImage
          ref={imageRef}
          src={product.images[currentImageIndex]}
          alt={`${product.name} auto part - Premium quality export component`}
          className="w-full h-[400px] object-cover rounded-lg"
          onLoad={() => handleImageLoad(currentImageIndex)}
        />
      </div>
      {product.images.length > 1 && (
        <>
          <button 
            onClick={handlePrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100 transition-colors"
            aria-label="Previous image"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button 
            onClick={handleNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100 transition-colors"
            aria-label="Next image"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}
    </div>
  );
}
